// Details for one gate: its numbers, its entries and exits over time, and its queue time.
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/atoms/Icon';
import { StatTile } from '../../Concession/components/ConcessionUI';
import GateIcon from './GateIcon';
import GatesFlowChart from '../charts/GatesFlowChart';
import GateQueueChart from '../charts/GateQueueChart';
import { queueColor, minuteToClock, formatRate } from '../gatesMetrics';

const GateModal = ({ stats, isLive, currentGame, range = null, onClose }) => {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!stats) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [stats, onClose]);

  if (!stats) return null;
  const { gate, label, counts, queue, entrances, exits, queueNow, queuePeak, entryRate, exitRate } = stats;

  return createPortal(
    <div className="gx-modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gx-modal" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={dialogRef}>
        <div className="gx-modal-head">
          <GateIcon imageURL={gate.imageURL} label={label} className="gx-modal-icon" />
          <h2 className="gx-modal-title">{label}</h2>
          <button type="button" className="gx-modal-close" onClick={onClose} aria-label="Close">
            <Icon name="x-lg" />
          </button>
        </div>

        <div className="gx-modal-body">
          <div className="cx-stat-grid gx-modal-stats">
            <StatTile
              label="Entrances"
              value={entrances.toLocaleString()}
              caption={isLive ? `${formatRate(entryRate)} / min now` : undefined}
            />
            <StatTile
              label="Exits"
              value={exits.toLocaleString()}
              caption={isLive ? `${formatRate(exitRate)} / min now` : undefined}
            />
            {isLive && (
              <StatTile
                label="Queue now"
                value={queueNow ? `${queueNow.value} min` : '—'}
                accent={queueNow ? queueColor(queueNow.value) : undefined}
                caption={queueNow ? `As of ${minuteToClock(queueNow.minute)}` : 'No recent reading'}
              />
            )}
            <StatTile
              label="Peak queue"
              value={queuePeak ? `${queuePeak.value} min` : '—'}
              accent={queuePeak ? queueColor(queuePeak.value) : undefined}
              meta={queuePeak ? minuteToClock(queuePeak.minute) : undefined}
              caption={queuePeak ? undefined : 'No queue readings'}
            />
          </div>

          <GatesFlowChart series={counts} currentGame={currentGame} range={range} who={label} height={260} />
          {queue.length > 0 && <GateQueueChart series={queue} currentGame={currentGame} range={range} />}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GateModal;
